import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  Plus,
  Search,
  Package,
  Wrench,
  Truck,
  AlertTriangle,
  X,
  Edit,
  Trash2,
  ArrowRightLeft,
  CheckCircle,
  Clock,
  RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

const categoryOptions = ['tool', 'equipment', 'material', 'vehicle', 'safety'];
const conditionOptions = ['excellent', 'good', 'fair', 'needs_repair'];

const statusConfig = {
  available: { label: 'Available', color: 'bg-green-500/10 text-green-600 border-green-200', icon: CheckCircle },
  checked_out: { label: 'Checked Out', color: 'bg-blue-500/10 text-blue-600 border-blue-200', icon: Clock },
  maintenance: { label: 'Maintenance', color: 'bg-yellow-500/10 text-yellow-600 border-yellow-200', icon: Wrench },
  needs_purchase: { label: 'Needs Purchase', color: 'bg-red-500/10 text-red-600 border-red-200', icon: AlertTriangle }
};

export const Inventory = () => {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [checkouts, setCheckouts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [checkoutItem, setCheckoutItem] = useState(null);
  const [activeTab, setActiveTab] = useState('inventory');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [itemsRes, checkoutsRes, jobsRes] = await Promise.all([
        api.get('/inventory'),
        api.get('/inventory/checkouts'),
        api.get('/jobs')
      ]);
      setItems(itemsRes.data);
      setCheckouts(checkoutsRes.data);
      setJobs(jobsRes.data.filter(j => ['active', 'pursuing'].includes(j.status)));
    } catch (error) {
      console.error('Error fetching inventory:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          item.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !filterCategory || item.category === filterCategory;
    const matchesStatus = !filterStatus || item.status === filterStatus;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const handleSaveItem = async (itemData) => {
    try {
      if (editingItem) {
        await api.patch(`/inventory/${editingItem.id}`, itemData);
        toast.success('Item updated');
      } else {
        await api.post('/inventory', itemData);
        toast.success('Item added');
      }
      fetchData();
      setShowAddModal(false);
      setEditingItem(null);
    } catch (error) {
      toast.error('Failed to save item');
    }
  };

  const handleCheckout = async (checkoutData) => {
    try {
      await api.post('/inventory/checkout', checkoutData);
      toast.success('Item checked out');
      fetchData();
      setShowCheckoutModal(false);
      setCheckoutItem(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to checkout item');
    }
  };

  const handleReturn = async (checkoutId) => {
    try {
      await api.post(`/inventory/return/${checkoutId}`);
      toast.success('Item returned');
      fetchData();
    } catch (error) {
      toast.error('Failed to return item');
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'tool': return Wrench;
      case 'vehicle': return Truck;
      default: return Package;
    }
  };

  // Statistics
  const stats = {
    total: items.length,
    available: items.filter(i => i.status === 'available').length,
    checkedOut: items.filter(i => i.status === 'checked_out').length,
    totalValue: items.reduce((sum, i) => sum + (i.unit_cost * i.quantity), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl">Inventory & Equipment</h1>
          <p className="text-gray-500 mt-1">{items.length} items in inventory</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 bg-[#C6A87C] text-white px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
          data-testid="add-item-btn"
        >
          <Plus className="w-4 h-4" />
          Add Item
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-100 p-4">
          <p className="text-xs font-semibold uppercase text-gray-400 mb-1">Total Items</p>
          <p className="font-heading text-2xl">{stats.total}</p>
        </div>
        <div className="bg-green-50 border border-green-100 p-4">
          <p className="text-xs font-semibold uppercase text-green-600 mb-1">Available</p>
          <p className="font-heading text-2xl text-green-700">{stats.available}</p>
        </div>
        <div className="bg-blue-50 border border-blue-100 p-4">
          <p className="text-xs font-semibold uppercase text-blue-600 mb-1">Checked Out</p>
          <p className="font-heading text-2xl text-blue-700">{stats.checkedOut}</p>
        </div>
        <div className="bg-[#C6A87C]/10 border border-[#C6A87C]/20 p-4">
          <p className="text-xs font-semibold uppercase text-[#C6A87C] mb-1">Total Value</p>
          <p className="font-heading text-2xl text-[#B09265]">${stats.totalValue.toLocaleString()}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          <button
            onClick={() => setActiveTab('inventory')}
            className={`py-4 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'inventory'
                ? 'border-[#C6A87C] text-[#C6A87C]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Inventory ({items.length})
          </button>
          <button
            onClick={() => setActiveTab('checkouts')}
            className={`py-4 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'checkouts'
                ? 'border-[#C6A87C] text-[#C6A87C]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Active Checkouts ({checkouts.length})
          </button>
        </nav>
      </div>

      {activeTab === 'inventory' && (
        <>
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search inventory..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                data-testid="inventory-search"
              />
            </div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white min-w-[150px]"
            >
              <option value="">All Categories</option>
              {categoryOptions.map(cat => (
                <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white min-w-[150px]"
            >
              <option value="">All Statuses</option>
              {Object.entries(statusConfig).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          {/* Items Grid */}
          {filteredItems.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <AnimatePresence mode="popLayout">
                {filteredItems.map(item => {
                  const CategoryIcon = getCategoryIcon(item.category);
                  const StatusIcon = statusConfig[item.status]?.icon || Package;
                  return (
                    <motion.div
                      key={item.id}
                      layout
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className="bg-white border border-gray-100 p-5 hover:shadow-lg transition-shadow"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-100 flex items-center justify-center">
                            <CategoryIcon className="w-5 h-5 text-gray-500" />
                          </div>
                          <div>
                            <h3 className="font-medium">{item.name}</h3>
                            <p className="text-sm text-gray-500 capitalize">{item.category}</p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 text-xs font-semibold border ${statusConfig[item.status]?.color}`}>
                          {statusConfig[item.status]?.label}
                        </span>
                      </div>

                      {item.description && (
                        <p className="text-sm text-gray-600 mb-4 line-clamp-2">{item.description}</p>
                      )}

                      <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                        <div className="p-2 bg-gray-50">
                          <p className="text-xs text-gray-400">Quantity</p>
                          <p className="font-medium">{item.available_quantity} / {item.quantity}</p>
                        </div>
                        <div className="p-2 bg-gray-50">
                          <p className="text-xs text-gray-400">Unit Cost</p>
                          <p className="font-medium">${item.unit_cost.toLocaleString()}</p>
                        </div>
                        {item.serial_number && (
                          <div className="p-2 bg-gray-50 col-span-2">
                            <p className="text-xs text-gray-400">Serial #</p>
                            <p className="font-mono text-sm">{item.serial_number}</p>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2 pt-4 border-t border-gray-100">
                        {item.status === 'available' && item.available_quantity > 0 && (
                          <button
                            onClick={() => { setCheckoutItem(item); setShowCheckoutModal(true); }}
                            className="flex-1 flex items-center justify-center gap-2 py-2 text-xs font-semibold text-[#C6A87C] border border-[#C6A87C] hover:bg-[#C6A87C]/5"
                          >
                            <ArrowRightLeft className="w-4 h-4" />
                            Checkout
                          </button>
                        )}
                        <button
                          onClick={() => { setEditingItem(item); setShowAddModal(true); }}
                          className="flex items-center justify-center gap-2 py-2 px-3 text-xs font-semibold text-gray-600 hover:text-[#C6A87C]"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          ) : (
            <div className="text-center py-16 bg-gray-50">
              <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-400 mb-4">No inventory items found</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="text-[#C6A87C] font-semibold hover:underline"
              >
                Add your first item
              </button>
            </div>
          )}
        </>
      )}

      {activeTab === 'checkouts' && (
        <div className="bg-white border border-gray-100">
          {checkouts.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {checkouts.map(checkout => (
                <div key={checkout.id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-blue-50 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="font-medium">{checkout.item_name}</p>
                      <p className="text-sm text-gray-500">
                        Qty: {checkout.quantity} • Job: {checkout.job_name}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Checked out by {checkout.checked_out_by} on {new Date(checkout.checkout_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleReturn(checkout.id)}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-green-500 text-green-600 text-xs font-semibold uppercase hover:bg-green-50"
                    data-testid={`return-${checkout.id}`}
                  >
                    <RotateCcw className="w-4 h-4" />
                    Return
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No active checkouts</p>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Item Modal */}
      <AnimatePresence>
        {showAddModal && (
          <ItemModal
            item={editingItem}
            onClose={() => { setShowAddModal(false); setEditingItem(null); }}
            onSave={handleSaveItem}
          />
        )}
      </AnimatePresence>

      {/* Checkout Modal */}
      <AnimatePresence>
        {showCheckoutModal && checkoutItem && (
          <CheckoutModal
            item={checkoutItem}
            jobs={jobs}
            onClose={() => { setShowCheckoutModal(false); setCheckoutItem(null); }}
            onCheckout={handleCheckout}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Item Modal Component
const ItemModal = ({ item, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: item?.name || '',
    category: item?.category || 'tool',
    description: item?.description || '',
    serial_number: item?.serial_number || '',
    quantity: item?.quantity || 1,
    unit_cost: item?.unit_cost || 0,
    vendor: item?.vendor || '',
    sku: item?.sku || '',
    condition: item?.condition || 'good',
    is_owned: item?.is_owned !== false
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white w-full max-w-xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <h2 className="font-heading text-xl">{item ? 'Edit' : 'Add'} Inventory Item</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Item Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                placeholder="DeWalt Table Saw"
                data-testid="item-name-input"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
              >
                {categoryOptions.map(cat => (
                  <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Condition</label>
              <select
                value={formData.condition}
                onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
              >
                {conditionOptions.map(cond => (
                  <option key={cond} value={cond}>{cond.replace('_', ' ').charAt(0).toUpperCase() + cond.slice(1)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Quantity</label>
              <input
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                min="1"
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Unit Cost</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <input
                  type="number"
                  value={formData.unit_cost}
                  onChange={(e) => setFormData({ ...formData, unit_cost: parseFloat(e.target.value) || 0 })}
                  className="w-full pl-8 pr-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Serial Number</label>
              <input
                type="text"
                value={formData.serial_number}
                onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                placeholder="Optional"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Vendor</label>
              <input
                type="text"
                value={formData.vendor}
                onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                placeholder="Home Depot"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none resize-none"
                placeholder="Additional details..."
              />
            </div>

            <div className="md:col-span-2 flex items-center gap-3">
              <input
                type="checkbox"
                id="is_owned"
                checked={formData.is_owned}
                onChange={(e) => setFormData({ ...formData, is_owned: e.target.checked })}
                className="w-4 h-4 accent-[#C6A87C]"
              />
              <label htmlFor="is_owned" className="text-sm text-gray-600">Company owned (uncheck if needs to be purchased)</label>
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-200 text-gray-600 text-xs font-semibold uppercase"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-[#C6A87C] text-white text-xs font-semibold uppercase hover:bg-[#B09265]"
              data-testid="save-item-btn"
            >
              {item ? 'Update' : 'Add'} Item
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Checkout Modal
const CheckoutModal = ({ item, jobs, onClose, onCheckout }) => {
  const [formData, setFormData] = useState({
    item_id: item.id,
    job_id: '',
    quantity: 1,
    expected_return_date: '',
    notes: ''
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white w-full max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <h2 className="font-heading text-xl">Checkout Item</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); onCheckout(formData); }} className="p-6 space-y-4">
          <div className="bg-gray-50 p-4 mb-4">
            <p className="font-medium">{item.name}</p>
            <p className="text-sm text-gray-500">Available: {item.available_quantity}</p>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Assign to Job *</label>
            <select
              value={formData.job_id}
              onChange={(e) => setFormData({ ...formData, job_id: e.target.value })}
              required
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
              data-testid="checkout-job-select"
            >
              <option value="">Select a job...</option>
              {jobs.map(job => (
                <option key={job.id} value={job.id}>{job.client_name} - {job.project_type}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Quantity</label>
            <input
              type="number"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: Math.min(parseInt(e.target.value) || 1, item.available_quantity) })}
              min="1"
              max={item.available_quantity}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Expected Return Date</label>
            <input
              type="date"
              value={formData.expected_return_date}
              onChange={(e) => setFormData({ ...formData, expected_return_date: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none resize-none"
              placeholder="Optional notes..."
            />
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-200 text-gray-600 text-xs font-semibold uppercase"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-[#C6A87C] text-white text-xs font-semibold uppercase hover:bg-[#B09265]"
              data-testid="confirm-checkout-btn"
            >
              Checkout
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};
